import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, CircularProgress, Alert, Box } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQrCodeAccess } from '../../utils/useQrCodeAccess';
import { buildApiUrl } from '../../config/api';

export default function PhotoUploadPage() {
    const { loading, error: qrError, participantId, envelopeId, tokenValid, token, uuid } = useQrCodeAccess();
    const navigate = useNavigate();
    const location = useLocation();
    const { responseText, envelopeFilename: initialEnvelope, responseFilename: initialResponse } = location.state || {};
    const [envelopePhoto, setEnvelopePhoto] = useState(null);
    const [responsePhoto, setResponsePhoto] = useState(null);
    const [envelopePreview, setEnvelopePreview] = useState(initialEnvelope ? buildApiUrl(`images/${initialEnvelope}`) : null);
    const [responsePreview, setResponsePreview] = useState(initialResponse ? buildApiUrl(`images/${initialResponse}`) : null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const envelopeShort = uuid ? uuid.split('-')[0] : '';

    const [accessDenied, setAccessDenied] = useState(false);
    useEffect(() => {
        async function validateAccess() {
            if (tokenValid && participantId && uuid) {
                // 1. Lê a chave digitada pelo participante
                let accessKey = '';
                try {
                    accessKey = localStorage.getItem(`access_key_${uuid}`) || '';
                } catch {}

                // 2. Lê o nickname salvo no objeto qrcode
                let savedNickname = '';
                let qrcodeData = null;
                try {
                    qrcodeData = JSON.parse(localStorage.getItem(`qrcode_${uuid}`));
                    savedNickname = qrcodeData?.nickname || '';
                } catch {}

                // 3. Se não houver nickname salvo, buscar do backend e salvar
                if (!savedNickname) {
                    try {
                        const resp = await fetch(buildApiUrl(`qrcode/${uuid}`));
                        if (resp.ok) {
                            const data = await resp.json();
                            savedNickname = data.nickname || '';
                            // Atualiza localStorage
                            const updatedQrcode = { ...(qrcodeData || {}), nickname: savedNickname };
                            localStorage.setItem(`qrcode_${uuid}` , JSON.stringify(updatedQrcode));
                        }
                    } catch {}
                }

                // 4. Recarrega nickname após possível atualização
                try {
                    qrcodeData = JSON.parse(localStorage.getItem(`qrcode_${uuid}`));
                    savedNickname = qrcodeData?.nickname || '';
                } catch {}

                // 5. Valida: access_key_${uuid} === nickname
                if (!accessKey || !savedNickname || accessKey !== savedNickname) {
                    setAccessDenied(true);
                }
            }
        }
        validateAccess();
    }, [tokenValid, participantId, uuid]);

    if (loading) {
        return <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}><CircularProgress /></Container>;
    }
    if (qrError || tokenValid === false || accessDenied) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <Alert severity="error">{qrError || accessDenied ? 'Chave de acesso não informada ou incorreta.' : 'Token inválido. Acesso não autorizado.'}</Alert>
            </Container>
        );
    }
    if (tokenValid === null) {
        return null;
    }

    const handleFileChange = (event, setPhoto, setPreview) => {
        const file = event.target.files[0];
        if (file) {
            setPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setPhoto(null);
            setPreview(null);
        }
        event.target.value = null;
    };

    const handleSubmitPhotos = async () => {
        if (!location.state?.isEditing && (!envelopePhoto || !responsePhoto) && !initialEnvelope && !initialResponse) {
            setError('Por favor, envie ambas as fotos.');
            return;
        }
        if (location.state?.isEditing && !envelopePhoto && !responsePhoto) {
            navigate(`/review/${uuid}?token=${token}`, {
                state: {
                    ...location.state,
                    envelopeFilename: initialEnvelope,
                    responseFilename: initialResponse,
                    isEditing: false
                }
            });
            return;
        }
        setIsLoading(true);
        setError('');
        const formData = new FormData();
        if (envelopePhoto) formData.append('envelope_photo', envelopePhoto);
        if (responsePhoto) formData.append('response_photo', responsePhoto);
        try {
            const response = await fetch(buildApiUrl(`submit/photos/${participantId}/${envelopeId}`), {
                method: 'POST',
                body: formData,
            });
            if (!response.ok) {
                let errorMsg = `Erro ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.detail || errorMsg;
                } catch (e) { /* Ignore */ }
                throw new Error(errorMsg);
            }
            const result = await response.json();
            const reviewState = {
                ...location.state,
                responseText: responseText,
                envelopeFilename: result.envelope_filename || initialEnvelope,
                responseFilename: result.response_filename || initialResponse,
                isEditing: false
            };
            navigate(`/review/${uuid}?token=${token}`, { state: reviewState });
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>Upload de Fotos - Sessão Envelope {envelopeShort}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ textAlign: 'center', border: '1px dashed grey', p: 2, minWidth: '200px' }}>
                    <Typography variant="subtitle1">Foto do QR code</Typography>
                    {envelopePreview && <img src={envelopePreview} alt="Preview Envelope" style={{ maxHeight: '150px', maxWidth: '100%', display: 'block', margin: '10px auto' }} />}
                    <Button variant="contained" component="label" startIcon={<PhotoCamera />} sx={{ mt: 1 }}>
                        Selecionar/Tirar Foto
                        <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => handleFileChange(e, setEnvelopePhoto, setEnvelopePreview)} />
                    </Button>
                    {envelopePhoto && <Typography variant="caption" display="block" sx={{ mt: 1 }}>{envelopePhoto.name}</Typography>}
                </Box>
                <Box sx={{ textAlign: 'center', border: '1px dashed grey', p: 2, minWidth: '200px' }}>
                    <Typography variant="subtitle1">Foto da Resposta</Typography>
                    {responsePreview && <img src={responsePreview} alt="Preview Resposta" style={{ maxHeight: '150px', maxWidth: '100%', display: 'block', margin: '10px auto' }} />}
                    <Button variant="contained" component="label" startIcon={<PhotoCamera />} sx={{ mt: 1 }}>
                        Selecionar/Tirar Foto
                        <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => handleFileChange(e, setResponsePhoto, setResponsePreview)} />
                    </Button>
                    {responsePhoto && <Typography variant="caption" display="block" sx={{ mt: 1 }}>{responsePhoto.name}</Typography>}
                </Box>
            </Box>
            <Button
                variant="contained"
                onClick={handleSubmitPhotos}
                disabled={isLoading || (!location.state?.isEditing && (!envelopePhoto || !responsePhoto) && !initialEnvelope && !initialResponse)}
                fullWidth
            >
                {isLoading ? <CircularProgress size={24} /> : (location.state?.isEditing ? 'Salvar Fotos e Voltar para Revisão' : 'Enviar Fotos e Ir para Revisão')}
            </Button>
        </Container>
    );
}
